import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Ban, Loader2, PencilLine, ThumbsUp } from "lucide-react";

import {
  clipboardDataSchema,
  readTinaHitlSource,
} from "@/components/forms/load-from-clipboard/clipboard-data";
import { useResolveEventFormData } from "@/components/forms/load-from-clipboard/use-resolve-event-form-data";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEventCreateFormStore } from "@/pages/events/create/hooks/use-event-create-form";
import type { EventBasicInfoType } from "@/pages/events/schemas/event.create.form";
import {
  LineupCard,
  VenueCard,
} from "@/pages/tina/items/components/panels/hitl-panel/entity-cards";
import { buildTinaHitlEventPrefill } from "@/pages/tina/items/components/panels/hitl-panel/hitl-event-prefill";
import {
  readResolvedLineupCards,
  readResolvedVenueCard,
} from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import {
  VouchReleaseBanner,
  type VouchState,
  needsVouchConfirm,
  vouchPending,
  vouchSummary,
} from "@/pages/tina/items/components/panels/hitl-panel/vouch-release";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";

export function LowScoreResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl, getVouchPreview } = useItemsRepository();
  const { buildFormData } = useResolveEventFormData();
  const updateBasicInfo = useEventCreateFormStore((s) => s.updateBasicInfo);
  const updateTinaHitlSource = useEventCreateFormStore(
    (s) => s.updateTinaHitlSource,
  );
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [vouch, setVouch] = useState<VouchState>({ status: "loading" });

  // Approving this event also vouches for its venue, which releases every sibling
  // parked behind the same venue penalty that the credit clears. Ask Tina how many
  // before rendering the button — IMS can compute neither the venue key hash nor the
  // release predicate itself. A failed read is NOT the same as zero: it keeps the
  // confirmation dialog on, because an unknown blast radius is exactly when a curator
  // should be asked. Approve stays disabled until one of the two lands, so the button
  // never changes behaviour under the curator's cursor.
  useEffect(() => {
    let live = true;
    const markFailed = () => {
      // Disclosure only — never surface this as a failed action the curator did
      // not ask for. `failed` keeps Approve behind the dialog with cautious copy.
      if (live) setVouch({ status: "failed" });
    };
    // try/catch around the call itself, not just the promise: this read must never
    // be able to take the whole resolution panel down. A cinema screening can also
    // park at `low_score`, so this component mounts on more surfaces than the music
    // queue — and Tina answers `applies: false` for those, since only the music
    // score stage stamps a venue dedup key.
    try {
      void getVouchPreview(item.id)
        .then((preview) => {
          if (live) setVouch({ status: "ready", preview });
        })
        .catch(markFailed);
    } catch {
      markFailed();
    }
    return () => {
      live = false;
    };
    // Deliberately key on `item.id` only — the same trap `hitl-panel.tsx`'s
    // coalesced-cluster effect documents. `useItemsRepository()` returns a new object
    // literal with fresh function refs every render, so including `getVouchPreview`
    // re-runs this effect on every render and drives an unbounded fetch loop, since
    // each response calls setState. It is a stateless fetch wrapper, so the closure
    // captured at render time is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const venueCard = readResolvedVenueCard(item, hitlContext);
  const lineupCards = readResolvedLineupCards(item, hitlContext);
  const reviewPrefill = buildTinaHitlEventPrefill(item);

  async function approveAsIs() {
    setSubmitting(true);
    try {
      await resolveHitl(item.id, { resolution: { kind: "score.approve" } });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  async function reviewAndEdit() {
    if (!reviewPrefill) return;
    setSubmitting(true);
    try {
      // The prefill payload is clipboard-shaped; resolve venue/lineup/genres to
      // full entities via the repos (Firebase) exactly like the clipboard populate
      // flow, then seed the create-form store directly (no clipboard round-trip).
      // basic-info-step re-mounts off `basicInfoStep`; closeTinaHitlForCreatedEvent
      // fires once the event is created (autoCloseHitl source).
      const parsed = clipboardDataSchema.parse(reviewPrefill);
      const formData = await buildFormData(parsed);
      updateBasicInfo({
        ...formData,
        // A curator reviewing & editing a low-score event is an explicit
        // full-confidence signal — stamp confidence onto the create payload so
        // the resulting event lands at 1.0 (mirrors the approve-as-is path).
        metadata: { ...formData.metadata, confidence: 1 },
      } as EventBasicInfoType);
      updateTinaHitlSource(readTinaHitlSource(parsed));
      navigate("/events/create");
    } finally {
      setSubmitting(false);
    }
  }

  async function reject() {
    setSubmitting(true);
    try {
      await resolveHitl(item.id, {
        resolution_kind: "force_closed",
      });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {venueCard && (
        <VenueCard
          card={venueCard}
          openHref={
            venueCard.resolved.id
              ? `/venues/edit/${venueCard.resolved.id}`
              : undefined
          }
        />
      )}
      <LineupCard cards={lineupCards} />

      <VouchReleaseBanner state={vouch} />

      <div className="flex flex-wrap items-center gap-2">
        {needsVouchConfirm(vouch) ? (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                type="button"
                disabled={submitting || vouchPending(vouch)}
              >
                <ThumbsUp className="mr-1 h-4 w-4" />
                Approve as-is &amp; create
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Approve and vouch for this venue</DialogTitle>
                <DialogDescription>
                  {vouchSummary(vouch)} Each released event is still re-scored
                  on its own date, lineup and image — only the venue penalty is
                  removed. Reject closes just this event and releases nothing.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline" disabled={submitting}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="button"
                  onClick={approveAsIs}
                  disabled={submitting}
                >
                  {submitting && (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  )}
                  Approve &amp; release
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : (
          <Button
            type="button"
            disabled={submitting || vouchPending(vouch)}
            onClick={approveAsIs}
          >
            {submitting || vouchPending(vouch) ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <ThumbsUp className="mr-1 h-4 w-4" />
            )}
            Approve as-is &amp; create
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={!reviewPrefill || submitting}
          onClick={reviewAndEdit}
        >
          <PencilLine className="mr-1 h-4 w-4" />
          Review &amp; edit
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button type="button" variant="destructive" disabled={submitting}>
              <Ban className="mr-1 h-4 w-4" />
              Reject
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reject this event</DialogTitle>
              <DialogDescription>
                Force-closes the Tina item without creating a Mood event.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={submitting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                onClick={reject}
                disabled={submitting}
              >
                {submitting && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                Reject &amp; close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <span className="text-xs text-muted-foreground">
        Approve creates this event and vouches for its venue, releasing any
        event parked behind the same venue penalty · Review &amp; edit opens the
        create form prefilled · Reject closes this event only, and cascades to
        nothing.
      </span>
    </div>
  );
}
